import React, { useEffect, useState } from "react";

import { Loading, Error } from "./states";
import { months } from "./utils";

import { Bar } from "react-chartjs-2";

const YearlyStats = ({ id, data, title, info_boxes }) => {
  const [loading, setLoading] = useState(true);
  const [chartData, setChartData] = useState(null);
  const [error, setError] = useState(null);
  const [allData, setAllData] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);

  const colorPalette = [
    "#7E909A",
    "#1C4E80",
    "#A5D8DD",
    "#EA6A47",
    "#0091D5",
    "#B3C100",
    "#4CB5F5",
    "#D32D41",
    "#6AB187",
    "#DBAE58",
    "#488A99",
    "#AC3E31",
  ];

  useEffect(() => {
    const sortData = (data) => {
      try {
        if (!data) {
          throw new Error("No data found");
        }

        const groupedArray = Object.entries(
          data.reduce((acc, item) => {
            const { year, ...rest } = item;
            if (!acc[year]) acc[year] = [];
            acc[year].push(rest);
            return acc;
          }, {}),
        ).map(([year, data]) => ({ year: Number(year), data }));

        setAllData(groupedArray);
        setActiveIndex(groupedArray.length - 1);

        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    sortData(data);

    return () => {};
  }, [data]);

  useEffect(() => {
    setLoading(true);

    const setActiveData = ({ data, selected }) => {
      try {
        const latestData = data[selected];
        const datasets = latestData["data"].map((item, index) => ({
          label: months[item.month - 1] + " " + latestData.year,
          data: [item.production, item.p50, item.p90],
          borderWidth: 1,
          backgroundColor: colorPalette[index % colorPalette.length], // Cycle through colors
          borderColor: colorPalette[index % colorPalette.length], // Cycle through colors
        }));

        console.log(id);

        let productionLabel = id == 89 ? "Prod+Curtail" : "Production";

        setChartData({
          year: latestData.year,
          data: {
            labels: [productionLabel, "P50", "P90"],
            datasets: datasets,
          },
        });
        setLoading(false);
      } catch (error) {
        setError(error);
        setLoading(false);
      }
    };

    if (allData) {
      setActiveData({ data: allData, selected: activeIndex });
    }

    return () => {};
  }, [activeIndex]);

  const handleIndexChange = (direction) => {
    if (direction === "up") {
      setActiveIndex((prev) => prev + 1);
    } else {
      setActiveIndex((prev) => prev - 1);
    }
  };

  const calculateYAxisMax = (data) => {
    let maxStackValue = 0;

    // Loop through each data index (bar) and sum up stacked values
    data.labels.forEach((_, index) => {
      let stackTotal = 0;
      data.datasets.forEach((dataset) => {
        stackTotal += parseFloat(dataset.data[index]) || 0; // Sum all values in the stack
      });

      // Track the highest total stack
      if (stackTotal > maxStackValue) {
        maxStackValue = stackTotal;
      }
    });

    return maxStackValue * 1.1; // Increase max height by 20%
  };

  const options = {
    responsive: true,
    padding: 32,
    plugins: {
      legend: {
        display: true,
        position: "bottom",
      },
      colors: true,
      datalabels: {
        anchor: "end", // Position at the top of bars
        align: "top",
        color: "#000",
        font: {
          weight: "bold",
          size: 12,
        },
        display: (context) => {
          context.active ? "auto" : false;
        },
        formatter: (value, context) => {
          const datasetIndex = context.datasetIndex;
          const dataIndex = context.dataIndex;

          let total = 0;
          context.chart.data.datasets.forEach((dataset) => {
            total += parseFloat(dataset.data[dataIndex]) || 0;
          });

          return datasetIndex === context.chart.data.datasets.length - 1
            ? total
            : "";
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        title: {
          display: true,
          text: "Production (MWh)",
        },
        ticks: {
          beginAtZero: true,
        },
        suggestedMax: chartData ? calculateYAxisMax(chartData.data) : undefined,
      },
    },
    title: {
      display: true,
      text: "Bar Chart Data",
    },
  };

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return <Error message={error} />;
  }

  return (
    <>
      <span className="h3">{title}</span>
      <div className="component__graph-switcher">
        <div className="nav-item">
          {activeIndex > 0 && (
            <span onClick={() => handleIndexChange("down")}>Prev</span>
          )}
        </div>
        <div className="current">{chartData.year}</div>
        <div className="nav-item">
          {activeIndex < allData.length - 1 && (
            <span onClick={() => handleIndexChange("up")}>Next</span>
          )}
        </div>
      </div>
      {chartData && <Bar data={chartData.data} options={options} />}
      {info_boxes != null && (
        <div className="chart__info">
          <strong>Information</strong>
          {info_boxes.map((box, index) => (
            <div key={index}>{box.content}</div>
          ))}
        </div>
      )}
    </>
  );
};

export default YearlyStats;
